<template>
    <ContentComp>
        <FormLayout>
            <FormLayoutRow class="flex-fill">
                <FormLayoutLabel text="categorytemplate.name"></FormLayoutLabel>
                <DropdownSelect
                    class="flex-fill"
                    :show-search="true"
                    :data="computedTemplates"
                    v-model="selectedTemplate"
                />
            </FormLayoutRow>

            <SourceEvents
                v-if="internalPreviousEvents"
                class="flex-fill"
                v-model="sourceEventId"
                :eventslist="internalPreviousEvents"
            ></SourceEvents>
        </FormLayout>

        <ContestantEventDetailsView
            :categories="model"
            :hide-contestants="true"
        />
    </ContentComp>
</template>
<script lang="ts" setup>
import { DropdownSelect } from '@/components';
import { FormLayout, FormLayoutLabel, FormLayoutRow } from '@/components/Form';
import ContentComp from '@/components/layout/ContentComp.vue';
import { useTaskLoader } from '@/composeables';
import { BaseObjDao, CategoryDao, EventDao } from '@/dao';
import type { SelectLine, SelectLineValueType } from '@/objects';
import { Category, type CategoryTemplate, type EventinfoBase } from '@/objects/generated';
import { useEventCommonStoreRef, useUtilStore } from '@/stores';
import { computed, ref, watch } from 'vue';
import SourceEvents from '../EventInfo/SourceEvents.vue';
import ContestantEventDetailsView from '../Contestants/ContestantEventDetailsView.vue';

/**
 * This is the Popup view from the Dashboard for creating the first race in the event
 */

const { AddLoader, LoadState } = useTaskLoader();

const { Event } = useEventCommonStoreRef();

// The vmodel for the categories, this will be filled with the categories from the selected template or event
const model = defineModel<Category[]>({ required: true });

const props = withDefaults(
    defineProps<{
        /**
         * Contains a list of events we can select as a source for contestants
         */
        sportId?: number | null;

        previousEvents?: EventinfoBase[] | null;
    }>(),
    {
        sportId: null,
        previousEvents: null,
    },
);

// A list of the awailable category templates for the current sport
const categoryTemplates = ref<CategoryTemplate[]>([]);

// The selected template for the category
const selectedTemplate = ref<SelectLineValueType>(null);

// The selected source event ID for the categories
const sourceEventId = ref<number | null>(null);

// The list of templates available for the current sport, used in the dropdown
const computedTemplates = computed(() => {
    const cats = categoryTemplates.value.map((template) => {
        return { value: template.ID, text: template.Name } as SelectLine;
    });

    // Add a null value for "none"
    cats.unshift({ value: null, text: 'general.none' } as SelectLine);

    return cats;
});

const internalPreviousEvents = ref<EventinfoBase[] | null>(null);

/**
 * This will normalize the categories from one event to the other so we dont end up keeping some weird IDs
 * @param categories
 */
const processCategories = (categories: Category[]) => {
    categories
        .sort((a, b) => {
            let aGender = (a.Gender ?? 'n').toString();
            let bGender = (b.Gender ?? 'n').toString();
            return aGender.localeCompare(bGender) || a.Name.localeCompare(b.Name);
        })
        .forEach((x) => {
            x.SetEntityStateAsAddedAndResetId(useUtilStore().nextFakeId());
            x.CategoryGroupConnection = []; // We need to reset the category group connection
        });
    return categories;
};

// We watch for changes on the selectedTemplate and then load the categories for that template.
watch(selectedTemplate, async (nv, ov) => {
    if (nv == null) {
        return;
    }

    sourceEventId.value = null; // Reset the source event ID when changing template
    model.value = processCategories((await CategoryDao.GetCategoriesForTemplate(selectedTemplate.value as number)) ?? []);
});

// We watch for changes on the sourceEventId and then load the categories for that event.
watch(sourceEventId, async (nv, ov) => {
    if (nv == null || !sourceEventId.value) {
        return;
    }

    selectedTemplate.value = null; // Reset the selected template when changing source event
    model.value = processCategories((await CategoryDao.GetCategoriesForEvent(sourceEventId.value)) ?? []);
});

/**
 * This will load the data for the component, including the category templates and previous events.
 */
const loadData = async () => {
    selectedTemplate.value = null;
    await AddLoader(async () => {
        if (!props.sportId && !Event.value?.SportID) {
            categoryTemplates.value = [];
            return;
        }
        categoryTemplates.value = (await CategoryDao.GetCategoryTemplates(props.sportId ?? Event.value?.SportID ?? 0)).sort((a, b) => {
            return (a.Name ?? '').localeCompare(b.Name ?? '');
        });

        internalPreviousEvents.value =
            props.previousEvents ?? (await EventDao.GetPreviousEventsForOrganizer(36, props.sportId ?? Event.value?.SportID ?? 0));
    }, '');
};

watch(
    () => props.sportId,
    () => {
        loadData();
    },
    { immediate: true },
);
</script>
